import { useState } from 'react';
import { CheckOutlined, DownOutlined, LockOutlined, RightOutlined } from '@ant-design/icons';

import { Badge, Button, Select, Textarea } from '../../../components/ui';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { PHAN_QUYET, THU_TU_PHAN_QUYET } from '../constants';
import type { KetQuaTieuChi, PhanQuyet, SuaKetQua } from '../types';

export interface DongKetQuaProps {
  kq: KetQuaTieuChi;
  duocSua: boolean;
  dangLuu: boolean;
  onSua: (du: SuaKetQua) => void;
  /**
   * Người dùng được quyết gợi ý sửa (`phien:update`). Khác `duocSua`: ô ghi đè phán quyết chỉ mở ở bước rà
   * soát, còn tick gợi ý sửa mở cả sau khi bị trả lại và sau khi hoàn tất (đặc tả mục 13.5).
   */
  duocQuyetSua?: boolean;
  onQuyetSua?: (dongY: boolean | null) => void;
}

/**
 * Một tiêu chí trong bảng rà soát: phán quyết, bằng chứng, chỗ ghi đè.
 *
 * Bằng chứng hiện **nguyên văn trích dẫn kèm tệp và trang** — đó là thứ người thẩm định đối chiếu để quyết
 * định có tin phán quyết của AI hay không (đặc tả mục 2.2). Không có bằng chứng thì không có gì để đối
 * chiếu, nên dòng ấy nói rõ "AI không đưa trích dẫn nào".
 *
 * Mở dòng ra là **đánh dấu đã xem**: người dùng mở để đọc, và bắt họ bấm thêm một nút "tôi đã xem" sau khi
 * đã đọc là thêm một bước không nói thêm điều gì.
 */
export function DongKetQua({ kq, duocSua, dangLuu, onSua, duocQuyetSua, onQuyetSua }: DongKetQuaProps) {
  const { t, locale } = useT();
  const [mo, setMo] = useState(false);
  const [ghiChu, setGhiChu] = useState(kq.ghi_chu);

  const hieuLuc: PhanQuyet = kq.nguoi_phan_quyet ?? kq.ai_phan_quyet;
  const daGhiDe = kq.nguoi_phan_quyet !== null;
  const chuaXem = kq.can_xem && !kq.da_xem;

  function batMo() {
    const sau = !mo;
    setMo(sau);
    // Mở ra lần đầu một dòng cần xem = đã xem.
    if (sau && chuaXem && duocSua) onSua({ da_xem: true });
  }

  return (
    <li className={cn('border-b border-default last:border-0', chuaXem && 'bg-warning-50')}>
      <button
        type="button"
        aria-expanded={mo}
        className="flex w-full items-center gap-2 px-3 py-2 text-start hover:bg-subtle"
        onClick={batMo}
      >
        {mo ? <DownOutlined className="shrink-0 text-2xs text-content-muted" /> : <RightOutlined className="shrink-0 text-2xs text-content-muted" />}
        {kq.loai_tru && <LockOutlined className="shrink-0 text-warning-600" title={t('thamDinh.soan.batBuoc')} />}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm text-content-primary">{kq.ten}</span>
          <span className="block truncate text-2xs text-content-muted">{kq.ten_nhom}</span>
        </span>
        {chuaXem && <Badge variant="warning">{t('thamDinh.raSoat.canXem')}</Badge>}
        {daGhiDe && <Badge variant="info">{t('thamDinh.raSoat.daGhiDe')}</Badge>}
        <Badge variant={PHAN_QUYET[hieuLuc].variant}>{t(PHAN_QUYET[hieuLuc].khoa)}</Badge>
        {/* Độ tin cậy thấp là lý do dòng này phải xem — hiện ngay, không giấu trong phần mở rộng. */}
        <span className={cn('w-14 shrink-0 text-end text-2xs',
          kq.do_tin_cay < 0.6 ? 'text-warning-700' : 'text-content-muted')}>
          {soVN(Math.round(kq.do_tin_cay * 100), locale)}%
        </span>
      </button>

      {mo && (
        <div className="space-y-3 border-t border-default bg-surface px-4 py-3">
          {kq.ly_do && <p className="text-sm text-content-secondary">{kq.ly_do}</p>}

          <section aria-label={t('thamDinh.raSoat.bangChung')}>
            <p className="mb-1 text-2xs font-medium text-content-muted">{t('thamDinh.raSoat.bangChung')}</p>
            {kq.bang_chung.length === 0 ? (
              <p className="text-sm text-content-muted">{t('thamDinh.raSoat.khongBangChung')}</p>
            ) : (
              <ul className="space-y-1">
                {kq.bang_chung.map((b, i) => (
                  <li key={i} className="rounded border-s-2 border-primary-300 bg-subtle px-3 py-2">
                    <p className="text-sm italic text-content-primary">“{b.trich}”</p>
                    <p className="mt-1 text-2xs text-content-muted">
                      {b.tenTep}
                      {b.trang !== null && ` · ${t('thamDinh.raSoat.trang', { so: soVN(b.trang, locale) })}`}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {kq.goi_y_sua && (
            <section aria-label={t('thamDinh.raSoat.goiYSua')} className="rounded border border-warning-200 bg-warning-50 p-2">
              <p className="text-2xs font-medium text-warning-800">{t('thamDinh.raSoat.goiYSua')}</p>
              <p className="text-sm text-warning-700">{kq.goi_y_sua}</p>

              {/*
                * Ba nút, không hai: "Bỏ quyết định" là đường về `chua_quyet`. Người tick rồi đổi ý cần nó,
                * nếu không "từ chối" và "chưa xem" lẫn nhau (đặc tả mục 13.5).
                */}
              {duocQuyetSua && onQuyetSua && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    variant={kq.sua_trang_thai === 'dong_y' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => onQuyetSua(true)}
                  >
                    {t('thamDinh.raSoat.goiYSuaDongY')}
                  </Button>
                  <Button
                    variant={kq.sua_trang_thai === 'tu_choi' ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => onQuyetSua(false)}
                  >
                    {t('thamDinh.raSoat.goiYSuaTuChoi')}
                  </Button>
                  {kq.sua_trang_thai && kq.sua_trang_thai !== 'chua_quyet' && (
                    <Button variant="ghost" size="sm" onClick={() => onQuyetSua(null)}>
                      {t('thamDinh.raSoat.goiYSuaBoQuyet')}
                    </Button>
                  )}
                </div>
              )}
            </section>
          )}

          {duocSua && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex-1">
                <span className="mb-1 block text-2xs font-medium text-content-muted">
                  {t('thamDinh.raSoat.ghiDe')}
                </span>
                <Select<PhanQuyet | 'ai'>
                  className="w-full"
                  value={kq.nguoi_phan_quyet ?? 'ai'}
                  disabled={dangLuu}
                  aria-label={t('thamDinh.raSoat.ghiDe')}
                  options={[
                    { value: 'ai', label: t('thamDinh.raSoat.theoAi', { pq: t(PHAN_QUYET[kq.ai_phan_quyet].khoa) }) },
                    ...THU_TU_PHAN_QUYET.map((p) => ({ value: p, label: t(PHAN_QUYET[p].khoa) })),
                  ]}
                  // `ai` là gỡ ghi đè: gửi null để backend trả về phán quyết của AI.
                  onChange={(v) => onSua({ phan_quyet: v === 'ai' ? null : v })}
                />
              </label>
            </div>
          )}

          <label className="block">
            <span className="mb-1 block text-2xs font-medium text-content-muted">{t('thamDinh.raSoat.ghiChu')}</span>
            <Textarea
              rows={2}
              disabled={!duocSua || dangLuu}
              value={ghiChu}
              aria-label={t('thamDinh.raSoat.ghiChu')}
              onChange={(e) => setGhiChu(e.target.value)}
              onBlur={() => { if (ghiChu !== kq.ghi_chu) onSua({ ghi_chu: ghiChu }); }}
            />
          </label>

          {duocSua && !kq.da_xem && (
            <Button variant="outline" size="sm" icon={<CheckOutlined />} onClick={() => onSua({ da_xem: true })}>
              {t('thamDinh.raSoat.danhDauDaXem')}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
